import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import colors from "theme/colors";
import sizes from "theme/sizes";
import Text from "components/_UI/Text";
import { HorizontalCenter } from "styled/alignment/Center";
import { Column } from "styled/alignment/Column";
import { StyledForm } from "styled/UI/StyledForm";
import signInTexts from "texts/general/sign-in";
import Icon from "components/_UI/Icon";
import breakpoints from "theme/breakpoints";
import { useStepRegister } from "context/login/StepRegister";
import loginApi from "api/requests/login";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import Loader from "components/_UI/Loader";
import fieldValidations from "helpers/fieldValidations";
import paths from "routes/paths";
import { useUser } from "context/user/User";
import buttonText from "texts/buttons/buttonText";
import ConfirmEmailModal from "./ConfirmEmailModal";
import LinkPage from "components/_UI/LinkPage";
import { Row } from "styled/alignment/Row";
import routeParams from "theme/routeParams";
import OnboardingTags from "../sign-up/OnboardingTags";
import CompleteNameStep from "../sign-up/CompleteNameStep";
import useSocialLogin from 'hooks/useSocialLogin';
import useAppleSignIn from 'hooks/useAppleSignIn';
import SocialAuthButtons from 'components/_UI/SocialAuthButtons';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import {
  Title, TitleAccent, Subtitle, FieldGroup, FieldLabel, InputShell, IconBubble,
  TogglePassword, FieldError, SubmitButton, LoaderWrapper, FooterRow, FooterLink,
  TermsText, LinkTerms
} from '../common/AuthForm';

function SignIn() {
  const { setStepRegister } = useStepRegister();
  const { user, setUser } = useUser();

  const errorTokens = {
    unconfirmed_email: 'unconfirmed_email',
    pending_onboarding: 'pending_onboarding'
  }

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const redirectTo = searchParams.get('redirectTo') || paths.home;

  const {
    loading: buttonsLoading,
    errorMessage: socialErrorMessage,
    setErrorMessage: setSocialErrorMessage,
    showCompleteName,
    showPreferences,
    authenticate,
    completeName,
    completeTags
  } = useSocialLogin(redirectTo);

  const {
    ready: appleReady,
    signIn: appleSignIn,
    prepare: prepareApple,
    confirmNameDelivered
  } = useAppleSignIn();

  const [confirmEmail, setConfirmEmail] = useState(null)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [errorMessage, setErrorMessage] = useState("");
  const [errorType, setErrorType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false)
  const [showResendEmailMessage, setShowResendEmailMessage] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmPassword, setConfirPassword] = useState(null);
  const [isLoadingResendEmail, setIsLoadingResendEmail] = useState(false);
  const [isResendEmail, setIsResendEmail] = useState(true)
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const confirmEmail = searchParams.get('confirmEmail');
    const loginPartner = searchParams.get('loginWithPartner');

    // Caso o SignIn monte com loginWithPartner (quando não há sessão ativa)
    if (loginPartner == 1) {
      loginWithPartner()
      return
    }

    if (confirmEmail == 1) {
      setShowConfirmModal(true);
      return
    }

  }, []);

  const getCookie = (name) => {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find((c) => c.startsWith(`${name}=`));
    return cookie ? cookie.split('=')[1] : null;
  };

  const loginWithPartner = () => {
    const token = getCookie('token');
    if (!token) return;
    localStorage.setItem("token", token);

    // Limpa o parâmetro da URL para evitar re-execução
    searchParams.delete('loginWithPartner');
    const cleanSearch = searchParams.toString();
    const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`;
    window.history.replaceState({}, '', cleanUrl);

    window.location.reload();
  }

  const login = async ({ email, password }) => {
    try {
      setIsLoading(true);

      const { data } = await loginApi.signIn({
        email: email.toLowerCase(),
        password
      });

      localStorage.setItem("token", data.accessToken);

      searchParams.delete('redirectTo');
      navigate({
        pathname: redirectTo,
        search: new URLSearchParams(Object.fromEntries(searchParams)).toString()
      }, { replace: true });
      window.location.reload();

    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
      setErrorType(error?.response?.data?.type)
      setShowResendEmailMessage(false);

      if (error?.response?.data?.type === errorTokens.unconfirmed_email) {
        const userData = { email: email.toLowerCase(), password };
        setUser(userData); // Atualiza o estado para uso futuro
        resendConfirmCode(userData); // Passa o objeto direto para a função não vir vazio
        setShowConfirmModal(true);
      }

      if (error?.response?.data?.message === 'E-mail não verificado') {
        setUser({ email: email.toLowerCase(), password });
        setShowResendEmailMessage(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resendConfirmCode = async (userToResend) => {
    const targetUser = userToResend || user;

    if (!targetUser?.email) {
      setErrorMessage("Dados do usuário não encontrados para reenvio.");
      return;
    }

    setLoadingModal(true);
    try {
      await loginApi.resendConfirmCode(targetUser);
      setShowConfirmModal(true);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
    } finally {
      setLoadingModal(false);
      setIsLoadingResendEmail(false)
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse.credential) {
      setSocialErrorMessage('Erro ao entrar com Google');
      return;
    }

    await authenticate(
      () => loginApi.withGoogle({ credential: credentialResponse.credential }),
      'Erro ao entrar com Google'
    );
  };

  const handleAppleClick = async () => {
    setSocialErrorMessage('');

    try {
      const payload = await appleSignIn();

      // null = popup fechado pelo usuário. Cancelamento não é erro.
      if (!payload) {
        return;
      }

      const delivered = await authenticate(
        () => loginApi.withApple(payload),
        'Erro ao entrar com a Apple'
      );

      if (delivered) {
        confirmNameDelivered();
      }
    } catch (error) {
      setSocialErrorMessage(error.message);
    } finally {
      // O nonce é de uso único: a próxima tentativa precisa de um novo.
      prepareApple();
    }
  };

  const startSignUp = () => {
    setStepRegister(1)
    navigate({
      pathname: paths.register,
      search: new URLSearchParams(Object.fromEntries(searchParams)).toString()
    })
  }

  return (
    <Container>
      <BackButton>
        <StyledBackButton onClick={() => navigate(paths.home)}>
          <Icon name='back' color={colors.night} />
        </StyledBackButton>
      </BackButton>
      <BoxContainer fullyCentralized disabled={loadingModal}>
        {showCompleteName ? (
          <CompleteNameStep onComplete={completeName} hideBack />
        ) : showPreferences ? (
          <OnboardingTags onComplete={completeTags} hideBack />
        ) : (
          <>
            <LogoContainer>
              <Icon name={"whizr-inline-logo-dark"} />
            </LogoContainer>

            <Title>Que bom ter <TitleAccent>você aqui!</TitleAccent></Title>
            <Subtitle>Entre na sua conta para continuar<br />explorando o que te faz bem.</Subtitle>

            <StyledForm onSubmit={handleSubmit(login)}>
              <FieldGroup $first>
                <FieldLabel htmlFor="signin-email">{signInTexts.inputs.username}</FieldLabel>
                <InputShell $invalid={!!errors.email}>
                  <IconBubble aria-hidden="true"><Mail size={16} strokeWidth={1.8} /></IconBubble>
                  <input
                    id="signin-email"
                    type="email"
                    autoComplete="email"
                    placeholder="seu@email.com"
                    aria-invalid={errors.email ? 'true' : undefined}
                    {...register('email', fieldValidations.email())}
                  />
                </InputShell>
                {errors.email?.message && (
                  <FieldError role="alert">{errors.email?.message}</FieldError>
                )}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="signin-password">{signInTexts.inputs.password}</FieldLabel>
                <InputShell $invalid={!!errors.password}>
                  <IconBubble aria-hidden="true"><Lock size={19} strokeWidth={1.7} /></IconBubble>
                  <input
                    id="signin-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Digite sua senha"
                    aria-invalid={errors.password ? 'true' : undefined}
                    {...register('password', fieldValidations.passwordWithoutPatterns())}
                  />
                  <TogglePassword
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff size={18} strokeWidth={1.8} /> : <Eye size={18} strokeWidth={1.8} />}
                  </TogglePassword>
                </InputShell>
                {errors.password?.message && (
                  <FieldError role="alert">{errors.password?.message}</FieldError>
                )}
              </FieldGroup>

              {!isLoading ? (
                <SubmitButton type="submit" disabled={buttonsLoading}>
                  <span>{signInTexts.buttons.enter}</span>
                  <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
                </SubmitButton>
              ) : (
                <LoaderWrapper>
                  <Loader />
                </LoaderWrapper>
              )}

              {errorMessage && (
                <HorizontalCenter marginTop={sizes.big}>
                  <Text type={'bold'} color={colors.danger}> {errorMessage} </Text>
                </HorizontalCenter>
              )}
            </StyledForm>

            {!buttonsLoading && (errorType !== errorTokens.pending_onboarding && errorType !== errorTokens.unconfirmed_email) && (
              <ForgotPasswordRow>
                {signInTexts.texts.forgotPassword}{' '}
                <ForgotPasswordLink to={paths.recoverAccount}>
                  {signInTexts.texts.restoreAccount}
                </ForgotPasswordLink>
              </ForgotPasswordRow>
            )}

            <OrDivider>
              <OrLine />
              <OrText>ou</OrText>
              <OrLine />
            </OrDivider>

            {buttonsLoading ? (
              <Loader />
            ) : (
              <>
                <SocialSection>
                  <SocialAuthButtons
                    shape="glass"
                    appleReady={appleReady}
                    onGoogleSuccess={handleGoogleSuccess}
                    onGoogleError={() => setSocialErrorMessage('Erro ao entrar com Google')}
                    onAppleClick={handleAppleClick}
                  />
                </SocialSection>

                {socialErrorMessage && (
                  <HorizontalCenter marginTop='14px'>
                    <Text color={colors.danger}> {socialErrorMessage} </Text>
                  </HorizontalCenter>
                )}

                <FooterRow>
                  {signInTexts.texts.dontHaveAccount}{' '}
                  <FooterLink onClick={startSignUp}>
                    {signInTexts.buttons.newAccount}
                  </FooterLink>
                </FooterRow>

                <LinkCreatePartner to={'/quero-ser-parceiro'} target={'_blank'}>
                  Quer ser um parceiro do Whizr?
                </LinkCreatePartner>

                <TermsText>
                  Ao continuar você concorda com os <LinkTerms to={`${paths.terms.useTerms}`}>Termos de Uso</LinkTerms> e <LinkTerms to={`${paths.terms.policiePrivacy}`}>Política de Privacidade.</LinkTerms>
                </TermsText>

              </>
            )}

            {showConfirmModal && (
              <ConfirmEmailModal
                fromSignIn={true}
                setShowConfirmModal={setShowConfirmModal}
                onLogged={(e) => login(e)}
                resendConfirmCode={() => {
                  resendConfirmCode();
                  setIsLoadingResendEmail(true);
                }}
                isLoadingResendEmail={isLoadingResendEmail}
                isResendEmail={isResendEmail}
                setIsResendEmail={setIsResendEmail}
              />
            )}
          </>
        )}

      </BoxContainer>
    </Container>
  );
}

/* A coluna do formulário tem 441px e fica centralizada no painel. */

const Container = styled(Column)`
  align-items: center;
  width: 100%;
  min-height: 100vh;

  @media screen and (min-width: ${breakpoints.lg}) {
    min-height: 100%;
    padding: 78px 0 40px;
  }
`

const BoxContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 489px;
  padding: 8px 24px 32px;
    ${({ disabled }) => disabled && css`
        pointer-events: none;
    `}

  & > * {
    flex-shrink: 0;
  }

  @media screen and (min-width: ${breakpoints.lg}) {
    width: 441px;
    max-width: none;
    padding: 0;
  }
`;

const LogoContainer = styled.div`
  --home-logo-color: ${colors.homeAccent};
  width: 144px;
  height: 40px;
  margin-bottom: 34px;
`;

const ForgotPasswordRow = styled.p`
  font-size: 12.4px;
  line-height: 18px;
  color: #807C94;
  text-align: center;
  margin-top: 18px;
`;

const ForgotPasswordLink = styled(Link)`
  color: #6A3DD6;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: .8;
    text-decoration: underline;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-top: 26px;
`;

const OrLine = styled.div`
  flex: 1;
  height: 1px;
  background: #E6E2F6;
`;

const OrText = styled.span`
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  color: #797B8D;
`;

const SocialSection = styled.div`
  width: 100%;
  margin-top: 20px;
`

const LinkCreatePartner = styled(Link)`
  text-decoration: underline;
  color: #7A4DEB;
  margin-top: 10px;
  font-size: 11.6px;
  cursor: pointer;
`

const BackButton = styled(Row)`
  width: 100%;
  height: 40px;
  padding: 10px;
  margin-top: 24px;
  align-items: center;

  @media screen and (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const StyledBackButton = styled(Row)`
  width: 40px;
  height: 40px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.neutralDark};
  }
`;

export default SignIn;
